import React from 'react';
import { Box, Typography } from '@mui/material';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { loadGMaps } from '@/utils/load-gmaps';

interface MapOverviewProps {
  markers?: Array<{
    lat: number;
    lng: number;
    count: number;
    name?: string;
    id?: number;
  }>;
  clusterStyles?: {
    textColor?: string;
    backgroundColor?: string;
  };
}

export const MapOverview: React.FC<MapOverviewProps> = ({
  markers = [],
  clusterStyles: clusterStylesProps = {
    textColor: '#FFFFFF',
    backgroundColor: '#1A1A1A',
  },
}) => {
  const clusterStyles = React.useMemo(
    () => ({
      textColor: clusterStylesProps.textColor ?? '#FFFFFF',
      backgroundColor: clusterStylesProps.backgroundColor ?? '#1A1A1A',
    }),
    [clusterStylesProps.textColor, clusterStylesProps.backgroundColor]
  );

  React.useEffect(() => {
    const abortController = new AbortController();
    const googleMarkers: google.maps.Marker[] = [];
    const tooltips: Array<{ setMap: (map: google.maps.Map | null) => void }> = [];
    let clusterer: MarkerClusterer | undefined;

    (async () => {
      try {
        const GMaps = await loadGMaps();
        if (abortController.signal.aborted) return;
        const mapElement = document.getElementById('stats_overview_map');
        if (!mapElement) return;

      const map = new GMaps({
        el: '#stats_overview_map',
        lat: 50.8503, // Brussels coordinates
        lng: 4.3517,
        zoom: 12,
        styles: [
          {
            featureType: 'all',
            elementType: 'all',
            stylers: [{ saturation: -100 }, { lightness: 0 }],
          },
        ],
        scrollwheel: true,
        fullscreenControl: false,
        maxZoom: 18,
      });

      // Create custom tooltip class
      class CustomTooltip extends google.maps.OverlayView {
        position: google.maps.LatLng;
        containerDiv: HTMLDivElement;
        content: string;
        marker: google.maps.Marker;
        houseId?: number;

        constructor(
          position: google.maps.LatLng,
          content: string,
          marker: google.maps.Marker,
          houseId?: number
        ) {
          super();
          this.position = position;
          this.content = content;
          this.marker = marker;
          this.houseId = houseId;

          const containerDiv = document.createElement('div');
          containerDiv.style.position = 'absolute';
          containerDiv.style.visibility = 'hidden';
          containerDiv.innerHTML = `
            <div style="
              position: relative;
              font-family: 'Arial', sans-serif;
              min-width: 120px;
              cursor: pointer;
            ">
              <div style="
                background-color: white;
                padding: 16px 16px;
                border-radius: 15px;
                box-shadow: 0 2px 6px rgba(0,0,0,0.1);
                text-align: center;
                font-weight: 500;
                font-size: 14px;
                transition: transform 0.2s ease;
              ">
                ${content}
              </div>
              <div style="
                width: 0;
                height: 0;
                border-left: 8px solid transparent;
                border-right: 8px solid transparent;
                border-bottom: 8px solid white;
                position: absolute;
                top: -8px;
                left: 50%;
                transform: translateX(-50%);
              "></div>
            </div>
          `;

          this.containerDiv = containerDiv;

          containerDiv.addEventListener('click', (e) => {
            e.stopPropagation();
            window.open(`/admin/colocations/${this.houseId || ''}`);
          });

          containerDiv.addEventListener('mouseenter', () => {
            const tooltipContent = containerDiv.querySelector('div > div') as HTMLElement | null;
            if (tooltipContent) tooltipContent.style.transform = 'scale(1.02)';
          });

          containerDiv.addEventListener('mouseleave', () => {
            const tooltipContent = containerDiv.querySelector('div > div') as HTMLElement | null;
            if (tooltipContent) tooltipContent.style.transform = 'scale(1)';
          });
        }

        onAdd() {
          this.getPanes()!.floatPane.appendChild(this.containerDiv);
        }

        onRemove() {
          if (this.containerDiv.parentElement) {
            this.containerDiv.parentElement.removeChild(this.containerDiv);
          }
        }

        draw() {
          const position = this.getProjection().fromLatLngToDivPixel(this.position)!;
          this.containerDiv.style.left = position.x - this.containerDiv.offsetWidth / 2 + 'px';
          this.containerDiv.style.top = position.y + 30 + 'px';
        }

        hide() { this.containerDiv.style.visibility = 'hidden'; }
        show() { this.containerDiv.style.visibility = 'visible'; }
      }

      // Convert markers to Google Maps Marker objects
      const createdMarkers = markers.map((location) => {
        const marker = new google.maps.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: map.map,
          label: {
            text: String(location.count),
            color: clusterStyles.textColor,
            fontSize: '14px',
            fontWeight: 'bold',
            fontFamily: 'Vollkorn',
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 20,
            fillColor: 'gray',
            fillOpacity: 1,
            strokeWeight: 0,
          },
          cursor: 'pointer',
        });

        const tooltip = new CustomTooltip(
          new google.maps.LatLng(location.lat, location.lng),
          location.name || 'No name available',
          marker,
          location.id
        );
        tooltip.setMap(map.map);
        tooltips.push(tooltip);

        marker.addListener('mouseover', () => tooltip.show());
        marker.addListener('mouseout', () => tooltip.hide());
        if (location.id) {
          marker.addListener('click', () => window.open(`/admin/colocations/${location.id}`));
        }

        return marker;
      });

      googleMarkers.push(...createdMarkers);

      // Initialize MarkerClusterer with custom renderer
      clusterer = new MarkerClusterer({
        map: map.map,
        markers: createdMarkers,
        renderer: {
          render: ({ count, position }) => {
            return new google.maps.Marker({
              position,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 25,
                fillColor: clusterStyles.backgroundColor,
                fillOpacity: 1,
                strokeWeight: 0,
              },
              label: {
                text: String(count),
                color: clusterStyles.textColor,
                fontSize: '14px',
                fontWeight: 'bold',
                fontFamily: 'Vollkorn',
              },
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            });
          },
        },
      });

      if (markers.length === 1) {
        map.fitLatLngBounds(markers);
        map.panTo(markers[0]);
        map.setZoom(13);
      } else if (markers.length > 1) {
        map.fitLatLngBounds(markers);
      }
      } catch (err) {
        console.error('MapOverview failed to load', err);
      }
    })();

    return () => {
      abortController.abort();
      tooltips.forEach((tooltip) => tooltip.setMap(null));
      googleMarkers.forEach((marker) => marker.setMap(null));
      if (clusterer) clusterer.clearMarkers();
    };
  }, [markers, clusterStyles]);

  return (
    <Box className="w-full rounded-[16px] bg-white p-4 shadow-small">
      <Typography className="mb-6 font-Vollkorn text-lead1 font-bold leading-[27px] text-[#111827]">
        Map Overview
      </Typography>
      <div
        id="stats_overview_map"
        style={{
          height: '400px',
          width: '100%',
          borderRadius: '8px',
          position: 'relative',
        }}
      />
    </Box>
  );
};
